import { useState } from "react";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import { PageSection, PageTitle, PageSubtitle } from "../components/PageLayout";

const NAME_MAX = 80;
const EMAIL_MAX = 120;
const SUBJECT_MAX = 120;
const MESSAGE_MAX = 1500;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Form = styled.form`
  display: grid;
  gap: 16px;
`;

const Label = styled.label`
  font-weight: 600;
`;

const Input = styled.input`
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
`;

const TextArea = styled.textarea`
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
  min-height: 140px;
`;

const Submit = styled.button`
  justify-self: flex-start;
  padding: 12px 20px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: none;
  background: ${({ theme }) => theme.accent};
  color: ${({ theme }) => theme.accentContrast};
  font-weight: 600;
  cursor: pointer;
`;

const ErrorText = styled.span`
  color: #d64545;
  font-size: ${({ theme }) => theme.typography.small};
`;

function Contact() {
  const [errors, setErrors] = useState({});

  const handleSubmit = (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const name = (formData.get("name") || "").trim();
    const email = (formData.get("email") || "").trim();
    const subjectInput = (formData.get("subject") || "").trim();
    const message = (formData.get("message") || "").trim();

    const nextErrors = {};
    if (!name) {
      nextErrors.name = "Please enter your name.";
    } else if (name.length > NAME_MAX) {
      nextErrors.name = `Name must be ${NAME_MAX} characters or fewer.`;
    }

    if (!email) {
      nextErrors.email = "Please enter your email.";
    } else if (email.length > EMAIL_MAX) {
      nextErrors.email = `Email must be ${EMAIL_MAX} characters or fewer.`;
    } else if (!EMAIL_REGEX.test(email)) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (!subjectInput) {
      nextErrors.subject = "Please add a subject.";
    } else if (subjectInput.length > SUBJECT_MAX) {
      nextErrors.subject = `Subject must be ${SUBJECT_MAX} characters or fewer.`;
    }

    if (!message) {
      nextErrors.message = "Let us know how we can help.";
    } else if (message.length > MESSAGE_MAX) {
      nextErrors.message = `Message must be ${MESSAGE_MAX} characters or fewer.`;
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setErrors({});

    const safeSubject = encodeURIComponent(subjectInput || "Texmu Support");
    const body = encodeURIComponent(`${message}\n\nFrom: ${name} (${email})`);
    window.location.href = `mailto:solocreater8919@gmail.com?subject=${safeSubject}&body=${body}`;
  };

  return (
    <main id="main-content">
      <Helmet>
        <title>Contact Texmu Support</title>
        <meta name="description" content="Get in touch with the Texmu team for support or partnership opportunities." />
      </Helmet>
      <PageSection>
        <PageTitle>Contact Support</PageTitle>
        <PageSubtitle>Need help, want to partner, or have feedback? We&apos;d love to hear from you.</PageSubtitle>
        <Form onSubmit={handleSubmit} noValidate>
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            name="name"
            placeholder="Jane Doe"
            required
            maxLength={NAME_MAX}
            aria-invalid={Boolean(errors.name)}
          />
          {errors.name && <ErrorText role="alert">{errors.name}</ErrorText>}

          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            inputMode="email"
            placeholder="you@example.com"
            required
            maxLength={EMAIL_MAX}
            aria-invalid={Boolean(errors.email)}
            pattern={EMAIL_REGEX.source}
          />
          {errors.email && <ErrorText role="alert">{errors.email}</ErrorText>}

          <Label htmlFor="subject">Subject</Label>
          <Input
            id="subject"
            name="subject"
            placeholder="Brief summary"
            required
            maxLength={SUBJECT_MAX}
            aria-invalid={Boolean(errors.subject)}
          />
          {errors.subject && <ErrorText role="alert">{errors.subject}</ErrorText>}

          <Label htmlFor="message">How can we help?</Label>
          <TextArea
            id="message"
            name="message"
            placeholder="Share details so we can assist quickly."
            required
            maxLength={MESSAGE_MAX}
            aria-invalid={Boolean(errors.message)}
          />
          {errors.message && <ErrorText role="alert">{errors.message}</ErrorText>}

          <Submit type="submit">Send message</Submit>
        </Form>
      </PageSection>
    </main>
  );
}

export default Contact;
